import React, { useEffect, useState } from 'react';
import { FolderKanban, Plus } from 'lucide-react';
import {
  CollaboratorAuthService,
  type CollaboratorPortalAccess,
  type CollaboratorPortalProject
} from '../../../lib/services/collaboratorAuthService';
import { SectionToggle } from '../../shared/activity-drawer/ui/SectionToggle';
import { CreateAccessForm } from './CreateAccessForm';
import { EditAccessForm } from './EditAccessForm';
import { generatePassword } from './generatePassword';
import { ProjectsPortalAccessDetails } from './ProjectsPortalAccessDetails';
import { portalAccessKind } from './portalAccessKind';
import type { ExternalPartner } from '../../../lib/types';

export interface PartnerProjectsPortalSectionProps {
  partner: ExternalPartner;
  open: boolean;
  onToggle(): void;
  reloadToken: number;
  onDataChanged(): void;
}

/**
 * Sezione "Portale progetti" del tab Info partner, in stile Notion (mockup approvato,
 * colonna "Dopo"). Sostituisce CollaboratorPortalAccessManager.tsx mantenendone stati e
 * azioni (creazione, modifica, copia credenziali, nuova password, disattiva/riattiva)
 * ma senza box colorati: dati come PropRow, azioni come righe fantasma
 * (vedi ProjectsPortalAccessDetails per il rendering quando esiste un accesso).
 *
 * Nota: "Copia credenziali" e "Nuova password" erano un'unica azione ambigua nel manager
 * originale (generava silenziosamente una password se non ce n'era una). Ora sono due
 * azioni distinte: "Copia credenziali" è attiva solo se esiste già una password generata
 * in sessione (altrimenti resta disabilitata con un suggerimento), "Nuova password" è
 * l'unico percorso che ne genera una — con conferma esplicita, perché disconnette il
 * collaboratore.
 */
export function PartnerProjectsPortalSection({ partner, open, onToggle, reloadToken, onDataChanged }: PartnerProjectsPortalSectionProps): JSX.Element {
  const [access, setAccess] = useState<CollaboratorPortalAccess | null>(null);
  const [projects, setProjects] = useState<CollaboratorPortalProject[]>([]);
  const [checking, setChecking] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [sessionPassword, setSessionPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiedField, setCopiedField] = useState<'credentials' | 'username' | null>(null);

  const portalUrl = `${window.location.origin}/collaborator-portal/login`;

  const loadAccess = async () => {
    setChecking(true);
    try {
      const list = await CollaboratorAuthService.getCollaboratorAccessList();
      // `collaborator_portal_access` è condivisa col portale partner e non ha un
      // discriminatore: `external_partner_id` da solo può far combaciare la riga dell'altra
      // sezione (vedi portalAccessKind.ts). Filtriamo anche per kind === 'collaboratore' per
      // non mostrare/gestire qui l'accesso "Portale partner" dello stesso partner.
      // Fix vero: colonna `portal_type` dedicata (follow-up).
      const partnerAccess = list.find((a) => a.external_partner_id === partner.id && portalAccessKind(a) === 'collaboratore') || null;
      setAccess(partnerAccess);
      setProjects(partnerAccess ? await CollaboratorAuthService.getCollaboratorProjects(partnerAccess.id) : []);
    } catch (error) {
      console.error('Error loading collaborator access:', error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.id, reloadToken]);

  const flashCopied = (field: 'credentials' | 'username') => {
    setCopiedField(field);
    setTimeout(() => setCopiedField((prev) => (prev === field ? null : prev)), 1500);
  };

  const copyCredentialsText = (password: string) =>
    `URL Portale: ${portalUrl}\nUsername: ${access?.username}\nPassword: ${password}`;

  // "Copia credenziali": attiva solo se esiste già una password generata in sessione
  // (il pulsante è disabilitato altrimenti — vedi ProjectsPortalAccessDetails). Non genera
  // mai una password da sola: quel percorso è solo "Nuova password", con conferma esplicita.
  const handleCopyCredentials = () => {
    if (!access || !sessionPassword) return;
    navigator.clipboard.writeText(copyCredentialsText(sessionPassword));
    flashCopied('credentials');
  };

  const handleNewPassword = async () => {
    if (!access) return;
    if (!confirm('Generare una nuova password? Il collaboratore verrà disconnesso.')) return;
    setBusy(true);
    try {
      const password = generatePassword();
      const success = await CollaboratorAuthService.resetCollaboratorPassword(access.id, password);
      if (success) {
        setSessionPassword(password);
        navigator.clipboard.writeText(copyCredentialsText(password));
        flashCopied('credentials');
        // Come per il gemello "Portale partner": ricarica per riflettere lo sblocco
        // tentativi/is_active fatto lato server da portal_set_password.
        await loadAccess();
        onDataChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async () => {
    if (!access) return;
    setBusy(true);
    try {
      const ok = access.is_active
        ? await CollaboratorAuthService.disableCollaboratorAccess(access.id)
        : await CollaboratorAuthService.enableCollaboratorAccess(access.id);
      if (ok) {
        await loadAccess();
        onDataChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const copyUsername = () => {
    if (!access) return;
    navigator.clipboard.writeText(access.username);
    flashCopied('username');
  };

  const summary = checking ? (
    <span className="min-w-0 truncate text-[12px] font-medium text-[#37352F] dark:text-white">…</span>
  ) : !access ? (
    <span className="min-w-0 truncate text-[12px] text-[rgba(55,53,47,0.45)] dark:text-white/40">Nessun accesso</span>
  ) : access.is_active ? (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-[#37352F] dark:text-white">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0F7B6C] dark:bg-[#4DB6A5]" />
      <span className="truncate">{access.username}</span>
    </span>
  ) : (
    <span className="inline-flex min-w-0 items-center gap-1.5 truncate text-[12px] font-medium text-[#37352F] dark:text-white">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#9B9A97]" />
      <span className="truncate">{access.username} · disattivato</span>
    </span>
  );

  return (
    <>
      <SectionToggle
        open={open}
        onToggle={onToggle}
        icon={<FolderKanban className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
        title="Portale progetti"
        summary={summary}
        dataTour="partner-info-projects-portal"
      >
        {checking ? null : !access ? (
          <>
            <div className="px-1.5 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
              Il collaboratore vede i progetti assegnati, aggiorna lo stato delle task e commenta.
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowCreateForm(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowCreateForm(true); } }}
              className="flex items-center gap-1.5 px-1.5 py-1 rounded text-xs text-[#787774] dark:text-gray-400 cursor-pointer hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] hover:text-[#37352F] dark:hover:text-white"
            >
              <Plus className="h-3 w-3" strokeWidth={1.5} />
              Crea accesso
            </div>
          </>
        ) : (
          <ProjectsPortalAccessDetails
            access={access}
            projects={projects}
            sessionPassword={sessionPassword}
            copiedField={copiedField}
            busy={busy}
            onCopyUsername={copyUsername}
            onCopyCredentials={handleCopyCredentials}
            onCopySessionPassword={() => { if (sessionPassword) { navigator.clipboard.writeText(sessionPassword); flashCopied('credentials'); } }}
            onNewPassword={handleNewPassword}
            onToggleActive={handleToggleActive}
            onEdit={() => setShowEditForm(true)}
          />
        )}
      </SectionToggle>

      {showCreateForm && (
        <CreateAccessForm
          partnerId={partner.id}
          partnerName={partner.name}
          onClose={() => setShowCreateForm(false)}
          onCreated={(password) => {
            setShowCreateForm(false);
            setSessionPassword(password);
            // La sezione può essere ancora chiusa (l'accesso si crea anche dal trailing
            // "Crea accesso" mentre è collassata): senza aprirla qui il callout con la
            // password appena generata non verrebbe mai renderizzato (SectionToggle monta
            // i children solo da aperto).
            if (!open) onToggle();
            loadAccess();
            onDataChanged();
          }}
        />
      )}

      {showEditForm && access && (
        <EditAccessForm
          access={access}
          currentProjects={projects}
          onClose={() => setShowEditForm(false)}
          onUpdated={() => {
            setShowEditForm(false);
            loadAccess();
            onDataChanged();
          }}
        />
      )}
    </>
  );
}
